<template>
  <div>
    <div v-if="!loading">
      <div style="padding: 20px;">
        <h1 style="font-size: 28px; font-weight: bold; margin-bottom: 30px;">Select a Route to Track</h1>
        
        <div v-if="routes.length > 0" class="route-grid">
          <div 
            v-for="(route, idx) in routes" 
            :key="idx" 
            :class="['route-card', { 'route-card-inactive': route.isActive === false }]"
            @click="navigateToRoute(route)"
          >
            <div class="route-card-content">
              <div class="route-logo">
                <img 
                  v-if="getRouteLogo(route)" 
                  :src="getRouteLogo(route)" 
                  :alt="route.name || route.route_name"
                  class="logo-image"
                  @error="handleImageError($event, route)"
                  @load="handleImageLoad"
                />
                <div 
                  :class="['logo-placeholder', { 'logo-hidden': getRouteLogo(route) && !route.logoError }]"
                  :ref="'placeholder-' + idx"
                >
                  <span class="logo-text">{{ getLogoInitials(route) }}</span>
                </div>
              </div>
              <div class="route-info">
                <div class="route-name">{{ expandDisneyAcronyms(formatRouteName(route.name || route.route_name || route.destination)) }}</div>
                <div v-if="route.timeSlots && route.timeSlots.length > 0" class="route-times">
                  <div v-for="(slot, slotIdx) in route.timeSlots" :key="slotIdx" class="time-slot">
                    <span class="time-label">{{ slot.label }}:</span>
                    <input 
                      type="text" 
                      :placeholder="slot.placeholder || ''" 
                      class="time-input"
                      @click.stop
                    />
                  </div>
                </div>
                <div v-else class="route-hours">
                  Operates on theme park hours.
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else style="padding: 40px; font-size: 20px; font-weight: bold;">
          <p>No routes are currently available.</p>
        </div>
      </div>
    </div>
    <div v-if="loading" style="padding: 40px; text-align: center;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>
</template>

<script>
import {get_tbn_actuals_fabbus, get_vehicle_track_fleet_tbn, get_vehicle_track_fleet_fabbus_route_summary} from '../networking/tbn_apis'
import moment from 'moment'
import {justDate} from '@/helpers/dates'

export default {
  name: 'vehicle_map_track_fleet_fabbus',
  components: {
  },
  metaInfo: {
    title: "Fabulous Buses - Select Route",
    titleTemplate: "Fabulous Buses",
  },
  data() {
    return {
      tenant_id: null,
      tenant_type: 'tbn',
      loading: true,
      routes: [],
      bookings: [],
      details_fromDate: justDate(moment()),
      details_toDate: justDate(moment()),
    }
  },
  async created() {
    this.check_features()
  },
  methods: {
    async check_features() {
      // First try to get tenant_id from route params (most reliable)
      if (this.$route.params.tenant_id) {
        this.tenant_id = this.$route.params.tenant_id
      } else {
        // Fallback to parsing the path
        let pathAfterFleetTrack = this.$route.path.split('/fleet-track-fabbus/')[1]
        if (!pathAfterFleetTrack) {
          pathAfterFleetTrack = this.$route.path.split('/fleet-track-fabbus')[1]
        }
        
        if (pathAfterFleetTrack) {
          const parts = pathAfterFleetTrack.split("/").filter(p => p && p !== 'tbn')
          this.tenant_id = parts[0] || null
        }
      }
      
      // Set tenant_type to tbn (this route is specifically for TBN)
      this.tenant_type = 'tbn'
      
      console.log('vehicle_map_track_fleet_fabbus: tenant_id =', this.tenant_id, 'route params:', this.$route.params, 'route path:', this.$route.path)
      
      this.load_data()
    },
    async load_data() {
      this.loading = true
      try {
        if (!this.tenant_id) {
          console.error('No tenant_id provided, cannot load routes')
          this.routes = []
          this.loading = false
          return
        }
        
        console.log('Loading routes for tenant_id:', this.tenant_id, 'date range:', this.details_fromDate, 'to', this.details_toDate)
        
        const {value: {data = []}} = await get_tbn_actuals_fabbus(
          'finish',
          moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), 
          moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        )

        console.log('API response:', data)
        this.summary = data.summary || {}
        this.bookings = data.results || []
        console.log('Loaded bookings:', this.bookings.length, 'bookings')
        
        // Process bookings to extract unique routes
        this.processRoutes()
        console.log('Processed routes:', this.routes.length, 'routes')
        
        // Check which routes have active vehicles
        await this.checkRouteStatus()
        
      } catch(e) {
        console.error('Error loading routes:', e)
        this.routes = []
      } finally {
        this.loading = false
      }
    },
    async checkRouteStatus() {
      if (!this.routes || this.routes.length === 0) return

      try {
        const {value: {data = {}}} = await get_vehicle_track_fleet_fabbus_route_summary()
        const routeCounts = data.routes || {}

        this.routes.forEach(route => {
          const routeIdentifier = route.route_name || route.name
          route.vehicleCount = routeCounts[routeIdentifier] || 0
          route.isActive = route.vehicleCount > 0
        })
      } catch(e) {
        console.error('Error checking route status:', e)
        this.routes.forEach(route => {
          route.isActive = false
          route.vehicleCount = 0
        })
      }

      this.routes.sort((a, b) => {
        if (a.isActive !== b.isActive) return b.isActive ? 1 : -1
        const nameA = (a.name || a.route_name || '').toLowerCase()
        const nameB = (b.name || b.route_name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })

      this.$forceUpdate()
    },
    isDateSpecificRoute(routeName) {
      if (!routeName) return false
      
      const name = routeName.toLowerCase()
      
      // Filter out supervisor routes
      if (name.includes('supervisor')) {
        return true
      }
      
      // Filter out one-way (O/W) and charter routes
      if (name.includes('one way') || name.includes('o/w') || name.includes('charter')) {
        return true
      }
      
      // Detect date patterns: MM/DD, MM-DD, DD/MM, DD-MM, etc.
      // Matches patterns like: 12/16, 12-16, 1/5, 01/05, etc.
      // This regex looks for 1-2 digits, followed by / or -, followed by 1-2 digits
      const datePattern = /\b\d{1,2}[\/\-]\d{1,2}\b/
      
      if (datePattern.test(routeName)) {
        return true
      }
      
      return false
    },
    processRoutes() {
      // Get unique routes from bookings data
      const routeMap = new Map()
      
      if (this.bookings && this.bookings.length > 0) {
        this.bookings.forEach(booking => {
          if ((booking.bookingType || '').toLowerCase() !== 'scheduledservice') return

          let routeKey = null
          let routeName = null
          
          if (booking.routeDescription) {
            routeKey = booking.routeDescription
            routeName = booking.routeDescription
          } else if (booking.bookingID) {
            routeKey = booking.bookingID.split('-')[0]
            routeName = routeKey
          }
          
          if (routeKey && this.isDateSpecificRoute(routeName)) return

          if (routeKey && !routeMap.has(routeKey)) {
            const route = {
              route_name: routeKey,
              name: routeName,
              bookingType: booking.bookingID ? booking.bookingID.split('-')[0] : null,
              destination: this.extractDestination(routeName),
              timeSlots: this.extractTimeSlots(routeName),
            }
            routeMap.set(routeKey, route)
          }
        })
      }
      
      // If no routes found, create a default empty state
      this.routes = Array.from(routeMap.values())
      
      // Sort routes alphabetically by name
      this.routes.sort((a, b) => {
        const nameA = (a.name || a.route_name || '').toLowerCase()
        const nameB = (b.name || b.route_name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    extractDestination(routeName) {
      // Extract destination from route name
      // This is a simple implementation - can be enhanced based on actual data structure
      if (!routeName) return ''
      
      // Remove common prefixes and clean up
      return routeName
        .replace(/^Before \d+:\d+ (am|pm) /i, '')
        .replace(/^After \d+:\d+ (am|pm) /i, '')
        .trim()
    },
    extractTimeSlots(routeName) {
      // Check if route has time slots (Before/After 10:45 am pattern)
      const beforeMatch = routeName.match(/Before (\d+:\d+ (am|pm))/i)
      const afterMatch = routeName.match(/After (\d+:\d+ (am|pm))/i)
      
      if (beforeMatch || afterMatch) {
        const slots = []
        if (beforeMatch) {
          slots.push({
            label: `Before ${beforeMatch[1]}`,
            placeholder: '',
          })
        }
        if (afterMatch) {
          slots.push({
            label: `After ${afterMatch[1]}`,
            placeholder: '',
          })
        }
        return slots
      }
      
      return null
    },
    extractTimeSlot(routeDescription) {
      // Extract time slot information from route description
      // Returns an object with label if time slot exists, null otherwise
      if (!routeDescription) return null
      
      const beforeMatch = routeDescription.match(/Before (\d+:\d+ (am|pm))/i)
      const afterMatch = routeDescription.match(/After (\d+:\d+ (am|pm))/i)
      
      if (beforeMatch) {
        return {
          label: `Before ${beforeMatch[1]}`,
          time: beforeMatch[1]
        }
      }
      
      if (afterMatch) {
        return {
          label: `After ${afterMatch[1]}`,
          time: afterMatch[1]
        }
      }
      
      return null
    },
    getRouteLogo(route) {
      // Only check route.route_name which contains the routeDescription from booking data
      // This ensures we only match on the actual route description, not derived fields
      const routeDescription = (route.route_name || '').toLowerCase()
      
      // If no route description, default to Fabulous Buses logo
      if (!routeDescription) {
        return 'https://fleet-integrations-web-assets.s3.us-west-2.amazonaws.com/logos/fabbus.png'
      }
      
      // Normalize route description - replace common abbreviations
      const normalizedDescription = routeDescription
        .replace(/lbv/g, 'lake buena vista')
        .replace(/\//g, ' ') // Replace slashes with spaces
        .replace(/\s+/g, ' ') // Normalize multiple spaces
        .trim()
      
      // Check for Hilton only - if "hilton" is not in route name, use default (fabbus) including for DoubleTree
      if (normalizedDescription.includes('hilton')) {
        return 'https://fleet-integrations-web-assets.s3.us-west-2.amazonaws.com/logos/hilton.png'
      }
      
      // Check for Hyatt - must be in route description
      if (normalizedDescription.includes('hyatt') || normalizedDescription.includes('grand cypress')) {
        return 'https://fleet-integrations-web-assets.s3.us-west-2.amazonaws.com/logos/hyatt.png'
      }
      
      // Check for Westgate - must be in route description
      if (normalizedDescription.includes('westgate')) {
        return 'https://fleet-integrations-web-assets.s3.us-west-2.amazonaws.com/logos/westgate.png'
      }
      
      // Default to Fabulous Buses logo if none of the above are found in route description
      return 'https://fleet-integrations-web-assets.s3.us-west-2.amazonaws.com/logos/fabbus.png'
    },
    handleImageError(event, route) {
      // Mark route as having logo error
      this.$set(route, 'logoError', true)
      // Hide broken image
      event.target.style.display = 'none'
    },
    handleImageLoad(event) {
      // Image loaded successfully, ensure placeholder is hidden
      const placeholder = event.target.nextElementSibling
      if (placeholder && placeholder.classList.contains('logo-placeholder')) {
        placeholder.style.display = 'none'
      }
    },
    formatRouteName(text) {
      if (!text) return text
      
      // If route already has an arrow, return as is
      if (text.includes('->') || text.includes('→')) {
        return text
      }
      
      // Common destination keywords (in order of specificity - longer first)
      const destinations = [
        'Disney Springs', 'Animal Kingdom', 'Hollywood Studios', 'Magic Kingdom',
        'Disney', 'Universal', 'SeaWorld', 'EPCOT',
        'MK', 'EP', 'AK', 'HS', 'DS', 'SW'
      ]
      
      // Check if the route contains a destination
      let destinationPart = ''
      let destinationKeyword = ''
      
      // Try to find destination (check longer/more specific first)
      for (const dest of destinations) {
        const regex = new RegExp(`\\b${dest.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i')
        if (regex.test(text)) {
          destinationKeyword = dest
          // Find where the destination appears
          const match = text.match(regex)
          if (match) {
            const destIndex = match.index
            // Get the destination part (from the keyword to the end, or to next slash)
            const afterDest = text.substring(destIndex)
            // If there's a slash after the destination, stop there
            const slashIndex = afterDest.indexOf('/')
            if (slashIndex !== -1) {
              destinationPart = afterDest.substring(0, slashIndex).trim()
            } else {
              destinationPart = afterDest.trim()
            }
            break
          }
        }
      }
      
      // If we found a destination, format as "Hotels -> Destination"
      if (destinationPart) {
        // Remove destination from text to get hotels part
        const hotelsPart = text.replace(destinationPart, '').replace(/\s*\/\s*$/, '').trim()
        
        if (hotelsPart) {
          // Clean up any trailing slashes or extra spaces
          const cleanHotels = hotelsPart.replace(/\s*\/\s*$/, '').trim()
          return `${cleanHotels} -> ${destinationPart}`
        }
      }
      
      return text
    },
    expandDisneyAcronyms(text) {
      if (!text) return text
      
      // Map of Disney acronyms to full names
      const acronymMap = {
        'AK': 'Animal Kingdom',
        'HS': 'Hollywood Studios',
        'MK': 'Magic Kingdom',
        'EP': 'EPCOT',
        'EPCOT': 'EPCOT', // Keep EPCOT as is
        'DS': 'Disney Springs',
        'LBV': 'Lake Buena Vista',
        'Hw': 'Hollywood',
        'HW': 'Hollywood',
        'Sw': 'SeaWorld',
        'SW': 'SeaWorld'
      }
      
      let expanded = text
      
      // Replace acronyms in parentheses like (AK/HS) or (MK/EP)
      expanded = expanded.replace(/\(([^)]+)\)/g, (match, content) => {
        const parts = content.split('/').map(part => {
          const trimmed = part.trim()
          return acronymMap[trimmed] || trimmed
        })
        return `(${parts.join(' / ')})`
      })
      
      // Replace standalone acronyms (with word boundaries)
      Object.keys(acronymMap).forEach(acronym => {
        // Match acronym as whole word (not part of another word)
        const regex = new RegExp(`\\b${acronym}\\b`, 'gi')
        expanded = expanded.replace(regex, acronymMap[acronym])
      })
      
      return expanded
    },
    getLogoInitials(route) {
      const name = route.name || route.route_name || route.destination || ''
      const words = name.split(' ').filter(w => w.length > 0)
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase()
      } else if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase()
      }
      return 'RT'
    },
    navigateToRoute(route) {
      // Use routeSearch mode - this will filter vehicles based on routeDescription in booking data
      // The backend will match vehicles to bookings with matching routeDescription
      const routeIdentifier = route.route_name || route.name
      if (routeIdentifier && this.tenant_id) {
        // Build the route path - Vue Router uses hash mode, so include # in the URL
        // Use routeSearch mode instead of bookingType - backend will filter by routeDescription
        // Include busicon in the path so that bus icons are used instead of arrows
        const routePath = `/fleet-track/${this.tenant_id}/busicon/routeSearch/${encodeURIComponent(routeIdentifier)}/tbn`
        // Construct full URL with hash for new tab
        const baseUrl = window.location.origin + window.location.pathname
        const hashPath = routePath.startsWith('#') ? routePath : '#' + routePath
        const fullUrl = baseUrl + hashPath
        console.log('Opening route:', fullUrl, 'Route identifier (routeDescription):', routeIdentifier)
        window.open(fullUrl, '_blank')
      } else {
        console.error('Missing route identifier or tenant_id:', { routeIdentifier, tenant_id: this.tenant_id, route })
      }
    },
    isMobile() {
      if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return true
      } else {
        return false
      }
    },
  }
}
</script>

<style scoped>
.route-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.route-grid > * {
  min-width: 0;
}

@media (max-width: 768px) {
  .route-grid {
    grid-template-columns: 1fr;
  }
}

.route-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-width: 0;
  width: 100%;
}

.route-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transform: translateY(-2px);
  border-color: #007ac7;
}

.route-card-inactive {
  background-color: #f5f5f5;
  opacity: 0.6;
  border-color: #ccc;
}

.route-card-inactive:hover {
  border-color: #999;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.route-card-inactive .route-name {
  color: #999;
}

.route-card-inactive .route-hours,
.route-card-inactive .time-label {
  color: #aaa;
}

.route-card-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.route-logo {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-placeholder {
  width: 60px;
  height: 60px;
  background-color: #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ddd;
}

.logo-hidden {
  display: none !important;
}

.logo-text {
  font-size: 18px;
  font-weight: bold;
  color: #666;
}

.route-info {
  flex: 1;
  min-width: 0;
}

.route-name {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  word-wrap: break-word;
}

.route-times {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.time-slot {
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-label {
  font-size: 14px;
  color: #666;
  min-width: 100px;
}

.time-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.route-hours {
  font-size: 14px;
  color: #666;
  font-style: italic;
}

h1 {
  text-align: center;
  color: #333;
}
</style>

